import { useEffect, useState } from 'react';

import EmailToolSettings from '../components/email-settings/EmailToolSettings';
import AlertMessage from '../components/alerts/AlertMessage';
import Loader from '../components/loader/Loader';
import { useAccount } from '../store/account/hooks';
import { useDomains } from '../store/domains/hooks';

const EmailSettings = () => {
  const { error, success, setError: setAccountError, setSuccess: setAccountSuccess } = useAccount();
  const { getDomains } = useDomains();
  const [domainsLoaded, setDomainsLoaded] = useState(false);

  // The domains slice is not persisted (see store/index.js whitelist), so it is
  // empty on a fresh load. FromSettings needs it to resolve the "From Address"
  // domain suffix, so fetch it here instead of relying on another page having run.
  //
  // The settings tree stays hidden until the fetch settles. We cannot gate on the
  // slice's own `loading` flag because it starts false, so the first paint would
  // still render the From Address field against an empty slice.
  useEffect(() => {
    let active = true;

    const loadDomains = async () => {
      try {
        await getDomains();
      } catch {
        // The thunk already routes failures to the domains slice and Sentry, so
        // the page only needs to stop waiting.
      } finally {
        // Reveal on failure too, so a rejected fetch cannot wedge the page on
        // the loader. The domains error is surfaced by the form itself.
        if (active) setDomainsLoaded(true);
      }
    };

    loadDomains();

    return () => {
      active = false;
    };
  }, [getDomains]);

  return (
    <div className="w-full flex-auto">
      <div className="mb-6 flex">
        <h1 className="text-h3 dark:text-white-100">Email Settings</h1>
      </div>

      {domainsLoaded ? (
        <EmailToolSettings />
      ) : (
        // Loader is absolutely positioned, so it needs a sized, relative parent.
        <div className="relative min-h-[300px]">
          <Loader />
        </div>
      )}

      <AlertMessage
        open={!!success}
        message={typeof success === 'string' ? success : 'Operation completed successfully!'}
        onClose={() => {
          setAccountSuccess(false);
        }}
        severity="success"
      />

      <AlertMessage
        open={!!error}
        message={typeof error === 'string' ? error : 'Oops, something went wrong!'}
        onClose={() => {
          setAccountError(false);
        }}
        severity="error"
      />
    </div>
  );
};

export default EmailSettings;
